import type { ProductCategory } from '~/types/products'
import { defineStore } from 'pinia'
import { useNuxtApp } from '#app'
import { baseStore } from '~/stores/Base'
import type { LlanaListResponse, LlanaSocketData, LlanaListOptions } from '~/types/llana'
function defaultList(): LlanaListResponse<ProductCategory> {
	return {
		data: [],
		total: 0,
		limit: default_limit,
		offset: 0,
	}
}

const table_name = 'product_categories'
type T = ProductCategory
const default_sort = 'name.asc'
const default_limit = 999
const default_relations = undefined

export class ProductCategoriesStore extends baseStore<T> {
	constructor() {
		super({
			table_name,
			store_name: table_name,
			default_sort,
			default_limit,
			default_relations,
		})
	}
}

export const useProductCategoriesStore = () =>
	defineStore(table_name, {
		state: () => <LlanaListResponse<T>>defaultList(),
		actions: {
			async bindEvents(where?: { [key: string]: string | number }) {
				const { $llanaSubscribe } = useNuxtApp()
				await $llanaSubscribe(
					table_name,
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.remove(data.id as number, where),
				)
			},
			async create(data: T) {
				const store = new ProductCategoriesStore()
				const { state, record } = await store.create(this.$state, data)
				this.$state = state
				return record
			},
			async get(id: number, where?: { [key: string]: string | number }): Promise<T | undefined> {
				const store = new ProductCategoriesStore()
				const { state, record } = await store.get(this.$state, id, where)
				this.$state = state
				return record
			},
			async list(options?: LlanaListOptions): Promise<LlanaListResponse<T>> {
				const store = new ProductCategoriesStore()
				this.$state = await store.list(this.$state, options)
				return this.$state
			},

			async search(options?: LlanaListOptions): Promise<LlanaListResponse<T>> {
				const store = new ProductCategoriesStore()
				return await store.search(options)
			},
			async reload(): Promise<LlanaListResponse<T>> {
				const store = new ProductCategoriesStore()
				this.$state = await store.reload()
				return this.$state
			},
			async update(id: number, data: Partial<T>): Promise<T> {
				const store = new ProductCategoriesStore()
				const { state, record } = await store.update(this.$state, id, data)
				this.$state = state
				return record
			},
			async remove(id: number, where?: { [key: string]: string | number }): Promise<void> {
				const store = new ProductCategoriesStore()
				this.$state = await store.remove(this.$state, id, where)
			},
			async delete(id: number): Promise<void> {
				const store = new ProductCategoriesStore()
				this.$state = await store.delete(this.$state, id)
			},
			async getByHandle(handle: string): Promise<ProductCategory | undefined> {
				const category = this.$state.data.find((c: ProductCategory) => c.handle === handle)
				if (category) {
					return category
				}

				const results = await this.search({
					where: [{ field: 'handle', value: handle, operator: 'equals' }],
					limit: 1,
					relations: default_relations,
				})

				if (results.data.length > 0) {
					const record = results.data[0]
					const index = this.$state.data.findIndex((c: ProductCategory) => c.id === record.id)
					if (index > -1) {
						this.$state.data[index] = record
					} else {
						this.$state.data.push(record)
					}
					return record
				}

				return undefined
			},
			async getChildDeepCategories(categoryId: number): Promise<ProductCategory[]> {
				const allCategories = this.$state.data as ProductCategory[]
				const childCategories: ProductCategory[] = []

				function findChildren(parentId: number) {
					const children = allCategories.filter(cat => cat.parent_id === parentId)
					for (const child of children) {
						childCategories.push({ ...child, slug: undefined })
						findChildren(child.id)
					}
				}

				findChildren(categoryId)
				return childCategories
			},

			getCategorySlug(categoryId: number): string {
				const allCategories = this.$state.data as ProductCategory[]
				const handles: string[] = []
				let currentCategory = allCategories.find(cat => cat.id === categoryId)

				while (currentCategory) {
					handles.unshift(currentCategory.handle)
					if (currentCategory.parent_id) {
						const parent = allCategories.find(cat => cat.id === currentCategory!.parent_id)
						currentCategory = parent || undefined
					} else {
						currentCategory = undefined
					}
				}

				return handles.join('/')
			},
		},
	})
